import React from 'react';
import type { Person } from './types';
import { User, MapPin, Heart, FileText, Camera, BookOpen } from 'lucide-react';

interface PersonCardProps {
  person: Person;
  onEdit?: (person: Person) => void;
  onViewDetails?: (person: Person) => void;
  isSelected?: boolean;
  compact?: boolean;
}

const PersonCard: React.FC<PersonCardProps> = ({ 
  person, 
  onEdit, 
  onViewDetails, 
  isSelected = false,
  compact = false 
}) => {
  const getGenderColor = (gender: string) => {
    switch (gender) {
      case 'male': return 'border-blue-300 bg-blue-50';
      case 'female': return 'border-pink-300 bg-pink-50';
      default: return 'border-purple-300 bg-purple-50';
    }
  };

  if (compact) {
    return (
      <div 
        className={`
          p-3 rounded-lg border-2 cursor-pointer transition-all hover:shadow-md
          ${getGenderColor(person.gender)}
          ${isSelected ? 'ring-2 ring-ocean-teal' : ''}
          min-w-[150px] max-w-[200px]
        `}
        onClick={() => onViewDetails?.(person)}
      >
        <div className="text-center">
          <User className="h-6 w-6 mx-auto mb-2 text-gray-600" />
          <h3 className="font-semibold text-sm text-gray-800 mb-1">{person.name}</h3>
          {person.birthDate && (
            <p className="text-xs text-gray-600">
              {person.birthDate.slice(0, 4)}
              {person.deathDate && ` - ${person.deathDate.slice(0, 4)}`}
            </p>
          )}
          {person.iwi && person.iwi.length > 0 && (
            <p className="text-xs text-purple-600 mt-1">{person.iwi[0]}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`
        p-6 rounded-lg border-2 cursor-pointer transition-all hover:shadow-lg
        ${getGenderColor(person.gender)}
        ${isSelected ? 'ring-2 ring-ocean-teal' : ''}
        max-w-sm
      `}
      onClick={() => onViewDetails?.(person)}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center">
          <User className="h-8 w-8 mr-3 text-gray-600" />
          <div>
            <h3 className="font-bold text-lg text-gray-800">{person.name}</h3>
            {person.birthDate && (
              <p className="text-sm text-gray-600">
                {person.birthDate}
                {person.deathDate && ` - ${person.deathDate}`}
              </p>
            )}
          </div>
        </div>
      </div>

      {(person.birthPlace || person.deathPlace) && (
        <div className="mb-3">
          <div className="flex items-center text-sm text-gray-600 mb-1">
            <MapPin className="h-4 w-4 mr-2" />
            <span>Places</span>
          </div>
          {person.birthPlace && (
            <p className="text-sm text-gray-700 ml-6">Born: {person.birthPlace}</p>
          )}
          {person.deathPlace && (
            <p className="text-sm text-gray-700 ml-6">Died: {person.deathPlace}</p>
          )}
        </div>
      )}

      {person.iwi && person.iwi.length > 0 && (
        <div className="mb-3">
          <div className="flex items-center text-sm text-gray-600 mb-1">
            <Heart className="h-4 w-4 mr-2" />
            <span>Iwi</span>
          </div>
          <div className="ml-6">
            {person.iwi.map((iwi, index) => (
              <span key={index} className="inline-block bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded mr-1 mb-1">
                {iwi}
              </span>
            ))}
          </div>
        </div>
      )}

      {person.hapu && person.hapu.length > 0 && (
        <div className="mb-3">
          <div className="flex items-center text-sm text-gray-600 mb-1">
            <Heart className="h-4 w-4 mr-2" />
            <span>Hapū</span>
          </div>
          <div className="ml-6">
            {person.hapu.map((hapu, index) => (
              <span key={index} className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded mr-1 mb-1">
                {hapu}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
        <div className="flex space-x-2">
          {person.photos && person.photos.length > 0 && (
            <Camera className="h-4 w-4 text-gray-400" />
          )}
          {person.documents && person.documents.length > 0 && (
            <FileText className="h-4 w-4 text-gray-400" />
          )}
          {person.stories && person.stories.length > 0 && (
            <BookOpen className="h-4 w-4 text-gray-400" />
          )}
        </div>
        
        {onEdit && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit(person);
            }}
            className="text-sm text-ocean-teal hover:text-ocean-deep font-medium"
          >
            Edit
          </button>
        )}
      </div>

      {person.notes && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <p className="text-sm text-gray-600 italic">{person.notes}</p>
        </div>
      )}
    </div>
  );
};

export default PersonCard;
